import { createSlice } from "@reduxjs/toolkit"
import axios from "axios"

const initialState = {
    // List products
    listProductsLoading: false,
    listProductsSuccess: false,
    listProductsFail: false,
    listProductsData: [],
    listProductsTotal: 0,
    listProductsPageCount: 0,
    listProductsErrorMessage: null,

    // Product categories (for the filter sidebar)
    listCategoriesData: [],

    // Distinct brands in use (for the filter sidebar)
    listBrandsData: [],

    // Get product detail
    getProductDetailLoading: false,
    getProductDetailSuccess: false,
    getProductDetailFail: false,
    getProductDetailData: null,
    getProductDetailErrorMessage: null,
}

export const productsSlice = createSlice({
    name: "products",
    initialState,
    reducers: {
        // List Products Actions
        listProductsRequest: (state) => {
            state.listProductsLoading = true
            state.listProductsSuccess = false
            state.listProductsFail = false
            state.listProductsErrorMessage = null
        },
        listProductsSuccessAction: (state, action) => {
            state.listProductsLoading = false
            state.listProductsSuccess = true
            state.listProductsFail = false
            state.listProductsData = action.payload.data
            state.listProductsTotal = action.payload.total ?? 0
            state.listProductsPageCount = action.payload.pageCount ?? 0
            state.listProductsErrorMessage = null
        },
        listProductsAppendAction: (state, action) => {
            state.listProductsLoading = false
            state.listProductsSuccess = true
            state.listProductsFail = false
            state.listProductsData = [...state.listProductsData, ...action.payload.data]
            state.listProductsTotal = action.payload.total ?? state.listProductsTotal
            state.listProductsPageCount = action.payload.pageCount ?? state.listProductsPageCount
            state.listProductsErrorMessage = null
        },
        listCategoriesSuccessAction: (state, action) => {
            state.listCategoriesData = action.payload
        },
        listBrandsSuccessAction: (state, action) => {
            state.listBrandsData = action.payload
        },
        listProductsFailAction: (state, action) => {
            state.listProductsLoading = false
            state.listProductsSuccess = false
            state.listProductsFail = true
            state.listProductsErrorMessage = action.payload
        },
        resetListProducts: (state) => {
            state.listProductsLoading = false
            state.listProductsSuccess = false
            state.listProductsFail = false
            state.listProductsErrorMessage = null
        },

        // Get Product Detail Actions
        getProductDetailRequest: (state) => {
            state.getProductDetailLoading = true
            state.getProductDetailSuccess = false
            state.getProductDetailFail = false
            state.getProductDetailErrorMessage = null
        },
        getProductDetailSuccessAction: (state, action) => {
            state.getProductDetailLoading = false
            state.getProductDetailSuccess = true
            state.getProductDetailFail = false
            state.getProductDetailData = action.payload
            state.getProductDetailErrorMessage = null
        },
        getProductDetailFailAction: (state, action) => {
            state.getProductDetailLoading = false
            state.getProductDetailSuccess = false
            state.getProductDetailFail = true
            state.getProductDetailErrorMessage = action.payload
        },
        resetGetProductDetail: (state) => {
            state.getProductDetailLoading = false
            state.getProductDetailSuccess = false
            state.getProductDetailFail = false
            state.getProductDetailData = null
            state.getProductDetailErrorMessage = null
        },
    },
})

// Action creators
export const {
    listProductsRequest,
    listProductsSuccessAction,
    listProductsAppendAction,
    listCategoriesSuccessAction,
    listBrandsSuccessAction,
    listProductsFailAction,
    resetListProducts,

    getProductDetailRequest,
    getProductDetailSuccessAction,
    getProductDetailFailAction,
    resetGetProductDetail,
} = productsSlice.actions

// Selectors
export const getListProductsLoading = (state) => state.products.listProductsLoading
export const getListProductsSuccess = (state) => state.products.listProductsSuccess
export const getListProductsFail = (state) => state.products.listProductsFail
export const getListProductsData = (state) => state.products.listProductsData
export const getListProductsTotal = (state) => state.products.listProductsTotal
export const getListProductsPageCount = (state) => state.products.listProductsPageCount
export const getListProductCategoriesData = (state) => state.products.listCategoriesData
export const getListProductBrandsData = (state) => state.products.listBrandsData
export const getListProductsErrorMessage = (state) => state.products.listProductsErrorMessage

export const getProductDetailLoading = (state) => state.products.getProductDetailLoading
export const getProductDetailSuccess = (state) => state.products.getProductDetailSuccess
export const getProductDetailFail = (state) => state.products.getProductDetailFail
export const getProductDetailData = (state) => state.products.getProductDetailData
export const getProductDetailErrorMessage = (state) => state.products.getProductDetailErrorMessage

// Thunks (API calls)

/* Query string for the product list, shared by doListProducts and doLoadMoreProducts.
 *
 * These two thunks used to carry byte-identical copies of this block. That is the exact shape
 * of a silent bug: add a filter to the first and forget the second, and page 1 is filtered
 * while every "Load more" page is not — appending unfiltered products onto a filtered grid,
 * with no error anywhere. One builder, used by both, removes the possibility.
 *
 * `brand` is appended once per value rather than comma-joined: brand names are free text and
 * can contain a comma, which URLSearchParams encodes identically to a separator. `bundle` is
 * safe to join because its only values are "0" and "1".
 */
const buildProductListParams = (filters) => {
    const params = new URLSearchParams()
    if (filters.category) params.append("category", filters.category)
    if (filters.bundle) params.append("bundle", filters.bundle)
    if (Array.isArray(filters.brand)) {
        filters.brand.forEach((b) => params.append("brand", b))
    } else if (filters.brand) {
        params.append("brand", filters.brand)
    }
    if (filters.sortBy) params.append("sortBy", filters.sortBy)
    if (filters.search) params.append("filter", filters.search)
    if (filters.minPrice) params.append("minPrice", filters.minPrice)
    if (filters.maxPrice) params.append("maxPrice", filters.maxPrice)
    if (filters.size) params.append("size", filters.size)
    if (filters.page !== undefined) params.append("page", filters.page)
    return params
}


/**
 * Get list of all products
 * @param {object} filters - { category, brand, bundle, sortBy, search, minPrice, maxPrice, page, size }
 */
export const doListProducts = (filters = {}) => {
    return async (dispatch) => {
        dispatch(listProductsRequest())

        try {
            const params = buildProductListParams(filters)

            const response = await axios.get(
                `/api/customer/products?${params.toString()}`
            )

            dispatch(listProductsSuccessAction({
                data: response.data.data,
                total: response.data.total_row,
                pageCount: response.data.page_count,
            }))
            return { payload: response.data.data }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Network error occurred"
            dispatch(listProductsFailAction(errorMessage))
            throw error
        }
    }
}

/**
 * Get active product categories for the filter sidebar
 */
export const doListProductCategories = () => {
    return async (dispatch) => {
        try {
            const response = await axios.get(`/api/customer/products/categories`)
            dispatch(listCategoriesSuccessAction(response.data.data))
        } catch (error) {
            // non-fatal — the sidebar just shows no categories
            console.error("Failed to load product categories:", error)
        }
    }
}

/**
 * Get the distinct brands in use, for the filter sidebar
 */
export const doListProductBrands = () => {
    return async (dispatch) => {
        try {
            const response = await axios.get(`/api/customer/products/brands`)
            dispatch(listBrandsSuccessAction(response.data.data))
        } catch (error) {
            // non-fatal — the sidebar just shows no brands, and nothing is filtered out
            console.error("Failed to load product brands:", error)
        }
    }
}

/**
 * Load more products (appends to existing list)
 * @param {object} filters - { category, brand, bundle, sortBy, search, minPrice, maxPrice, page, size }
 */
export const doLoadMoreProducts = (filters = {}) => {
    return async (dispatch) => {
        try {
            const params = buildProductListParams(filters)

            const response = await axios.get(
                `/api/customer/products?${params.toString()}`
            )

            dispatch(listProductsAppendAction({
                data: response.data.data,
                total: response.data.total_row,
                pageCount: response.data.page_count,
            }))
            return { payload: response.data.data }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Network error occurred"
            dispatch(listProductsFailAction(errorMessage))
            throw error
        }
    }
}

/**
 * Get product detail by ID
 * @param {string} productId - The product ID
 */
export const doGetProductDetail = (productId) => {
    return async (dispatch) => {
        dispatch(getProductDetailRequest())

        try {
            const response = await axios.get(
                `/api/customer/products/${productId}`
            )

            // if (response.data.success) {
                dispatch(getProductDetailSuccessAction(response.data.data))
            // } else {
            //     dispatch(getProductDetailFailAction(response.data.message || "Failed to fetch product detail"))
            // }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || "Network error occurred"
            dispatch(getProductDetailFailAction(errorMessage))
        }
    }
}

export default productsSlice.reducer